import React, { useState, useEffect } from "react";
import AdminTable from "../../Common/AdminTable";
import AdminDrawer from "../../Common/AdminDrawer";
import { getAuditLogs } from "../../../../services/adminService";
import { Eye, ShieldCheck, Filter } from "lucide-react";

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [inspectingLog, setInspectingLog] = useState(null);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await getAuditLogs({ module: moduleFilter, search });
      setLogs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [moduleFilter, search]);

  const columns = [
    {
      title: "Timestamp",
      key: "timestamp",
      width: "160px",
      render: (val) => (
        <span style={{ fontSize: "12px", color: "#64748b" }}>
          {new Date(val).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Admin User",
      key: "admin_user",
      width: "180px",
      render: (val) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{val}</span>,
    },
    {
      title: "Module",
      key: "module",
      width: "110px",
      render: (val) => (
        <span
          style={{
            fontSize: "11px",
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: "4px",
            background: "#f1f5f9",
            color: "#475569",
          }}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (val, row) => (
        <div>
          <span style={{ fontWeight: 600, color: "#0284c7" }}>{val}</span>
          <span style={{ fontSize: "12px", color: "#64748b", marginLeft: "8px" }}>
            ({row.entity_type}: {row.entity_id})
          </span>
        </div>
      ),
    },
    {
      title: "Changes",
      key: "inspect",
      width: "100px",
      render: (_, row) => (
        <button
          className="admin-btn admin-btn-secondary admin-btn-sm"
          onClick={() => setInspectingLog(row)}
        >
          <Eye size={13} /> View Diff
        </button>
      ),
    },
  ];

  return (
    <div>
      {/* Filter Header */}
      <div className="admin-card" style={{ marginBottom: "16px", padding: "14px 20px" }}>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={15} style={{ color: "#64748b" }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>
              Filter by Module:
            </span>
          </div>

          {["All", "Questions", "Tests", "Users", "Admin"].map((mod) => (
            <button
              key={mod}
              className={`admin-btn admin-btn-sm ${
                moduleFilter === mod ? "admin-btn-primary" : "admin-btn-secondary"
              }`}
              onClick={() => setModuleFilter(mod)}
            >
              {mod}
            </button>
          ))}
        </div>
      </div>

      <AdminTable
        columns={columns}
        data={logs}
        searchPlaceholder="Search audit records by admin user, entity ID, action..."
        searchQuery={search}
        onSearchChange={setSearch}
        pageSize={15}
        rowKey="id"
      />

      {/* Diff Inspector Drawer */}
      <AdminDrawer
        isOpen={Boolean(inspectingLog)}
        onClose={() => setInspectingLog(null)}
        title={inspectingLog ? `Audit Log Details: ${inspectingLog.id}` : ""}
        width="620px"
      >
        {inspectingLog && (
          <div>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid var(--admin-border)",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "13px" }}>
                <div><strong>Admin User:</strong> {inspectingLog.admin_user}</div>
                <div><strong>Action:</strong> {inspectingLog.action}</div>
                <div><strong>Entity:</strong> {inspectingLog.entity_type} ({inspectingLog.entity_id})</div>
                <div><strong>Time:</strong> {new Date(inspectingLog.timestamp).toLocaleString()}</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <h4 style={{ fontSize: "13px", fontWeight: 700, color: "#b91c1c", marginBottom: "8px" }}>
                  Before State
                </h4>
                <pre
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    padding: "12px",
                    borderRadius: "6px",
                    fontSize: "11.5px",
                    maxHeight: "350px",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {inspectingLog.before_value
                    ? JSON.stringify(inspectingLog.before_value, null, 2)
                    : "(None / Initial Creation)"}
                </pre>
              </div>

              <div>
                <h4 style={{ fontSize: "13px", fontWeight: 700, color: "#15803d", marginBottom: "8px" }}>
                  After State
                </h4>
                <pre
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    padding: "12px",
                    borderRadius: "6px",
                    fontSize: "11.5px",
                    maxHeight: "350px",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {inspectingLog.after_value
                    ? JSON.stringify(inspectingLog.after_value, null, 2)
                    : "(None / Deleted)"}
                </pre>
              </div>
            </div>
          </div>
        )}
      </AdminDrawer>
    </div>
  );
}
